import { z } from 'zod'
import * as Sentry from '@sentry/react'
import type { CanvasElement } from '../../domain/canvas/entities/element'
import { errorMessages } from '../shared/errorMessages'

/**
 * Add Element Use Case
 * Handles adding new elements to the canvas with proper positioning and validation
 */

// Command schemas for different element types
export const AddTextElementCommandSchema = z.object({
  type: z.literal('text'),
  centerInDesignZone: z.boolean().optional(),
  content: z.string().optional(),
  fontSize: z.number().optional(),
  fontFamily: z.string().optional(),
  color: z.string().optional(),
})

export const AddAssetElementCommandSchema = z.object({
  type: z.literal('asset'),
  src: z.string().url(),
  name: z.string(),
  assetId: z.string().uuid(), // Required by backend
  assetType: z.enum(['print', 'engrave']), // Required by backend
  color: z.string().optional(),
  centerInDesignZone: z.boolean().optional(),
  // Note: folderName and fileName removed - backend doesn't accept them
})

export const AddImageElementCommandSchema = z.object({
  type: z.literal('image'),
  src: z.string(),
  width: z.number(),
  height: z.number(),
  centerInDesignZone: z.boolean().optional(),
})

export const AddElementCommandSchema = z.discriminatedUnion('type', [
  AddTextElementCommandSchema,
  AddAssetElementCommandSchema,
  AddImageElementCommandSchema,
])

export type AddElementCommand = z.infer<typeof AddElementCommandSchema>

// Result type
export type AddElementResult = {
  success: boolean
  element?: CanvasElement & { position: { x: number; y: number }; rotation?: number }
  error?: string
}

// Dependencies interface
type AddElementDependencies = {
  getCurrentDesign: () => {
    zones: {
      design: { left: number; top: number; width: number; height: number }
      export: {
        left: number
        top: number
        width: number
        height: number
        centerPosition?: { x: number; y: number }
      }
    }
  } | null
  generateElementId: (type: string) => string
  constrainPosition?: (
    position: { x: number; y: number },
    element: Pick<CanvasElement, 'type' | 'dimensions'>,
  ) => { x: number; y: number }
}

// Factory function to create the use case
export const createAddElementUseCase = (deps: AddElementDependencies) => ({
  execute: async (command: AddElementCommand): Promise<AddElementResult> => {
    try {
      // Validate command
      const validatedCommand = AddElementCommandSchema.parse(command)

      // Generate unique element ID
      const elementId = deps.generateElementId(validatedCommand.type)

      // Calculate dimensions based on element type
      let dimensions: { width: number; height: number }

      switch (validatedCommand.type) {
        case 'text':
          dimensions = { width: 50, height: 16 } // Default text dimensions
          break
        case 'asset':
          dimensions = { width: 50, height: 50 } // Default asset dimensions
          break
        case 'image': {
          // Use original dimensions for images - scaling will be handled via transform
          dimensions = { width: validatedCommand.width, height: validatedCommand.height }
          break
        }
      }

      // Calculate position - positions are relative to export zone (0,0)
      let position = { x: 50, y: 50 } // Default position in mm

      const currentDesign = deps.getCurrentDesign()
      if (
        currentDesign &&
        ((validatedCommand.type === 'text' && validatedCommand.centerInDesignZone) ||
          (validatedCommand.type === 'image' && validatedCommand.centerInDesignZone) ||
          (validatedCommand.type === 'asset' && validatedCommand.centerInDesignZone))
      ) {
        const exportZone = currentDesign.zones.export
        // centerPosition is already an offset within the export zone
        // Element positions are stored relative to export zone (0,0)
        const centerX = exportZone.centerPosition?.x ?? exportZone.width / 2
        const centerY = exportZone.centerPosition?.y ?? exportZone.height / 2
        position = {
          x: centerX,
          y: centerY,
        }
      }

      // Apply position constraints if available
      if (deps.constrainPosition) {
        position = deps.constrainPosition(position, { type: validatedCommand.type, dimensions })
      }

      // Determine layer based on element type
      const layerMap = {
        image: 0, // Bottom layer
        asset: 1, // Middle layer
        text: 2, // Top layer
      }

      // Create base element
      const baseElement = {
        id: elementId,
        position,
        rotation: 0,
        transform: { rotation: 0, scaleX: 1, scaleY: 1, skewX: 0, skewY: 0 },
        locked: false,
        visible: true,
        layer: layerMap[validatedCommand.type] || 1,
        zIndex: 0,
      }

      let element: AddElementResult['element']

      switch (validatedCommand.type) {
        case 'text':
          element = {
            ...baseElement,
            type: 'text' as const,
            dimensions,
            content: validatedCommand.content || '',
            fontSize: validatedCommand.fontSize || 16,
            fontFamily: validatedCommand.fontFamily || 'Arial',
            fontWeight: 400,
            fontItalic: false,
            textAlign: 'center' as const,
            color: validatedCommand.color || '#000000',
          }
          break

        case 'asset':
          element = {
            ...baseElement,
            type: 'asset' as const,
            dimensions,
            src: validatedCommand.src,
            alt: validatedCommand.name,
            assetId: validatedCommand.assetId,
            assetType: validatedCommand.assetType,
            color: validatedCommand.color,
            // Note: folderName and fileName are NOT included - backend doesn't accept them
          }
          break

        case 'image':
          element = {
            ...baseElement,
            type: 'image' as const,
            dimensions,
            src: validatedCommand.src,
            alt: 'User uploaded image',
            value: validatedCommand.src,
            borderRadius: 0,
          }
          break
      }

      // TODO: Emit domain event
      // eventBus.emit(canvasEvents.elementAdded(element))

      return {
        success: true,
        element,
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'addElement' },
        extra: { elementType: command.type },
      })
      return {
        success: false,
        error: error instanceof Error ? error.message : errorMessages.failedToAddElement(),
      }
    }
  },
})
